/**
=========================================================
* Material Kit 2 PRO React - v2.1.1
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, lazy, Suspense } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// NotFound is eager — small, and the catch-all `*` route would otherwise
// pay a chunk-load round-trip just to render a 404.
import NotFound from "pages/NotFound";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Kit 2 PRO React themes
import theme from "assets/theme";

// Brand-aware root components (each loaded eagerly; only one will render per build)
import RingFoodsHomepage from "pages/RingFoodsHomepage";
import RingOperatorHomepage from "pages/RingOperatorHomepage";

// Lazy-loaded routes (code-split into separate chunks).
// AffiliateTerms serves both brands; its copy, canonical and plans come from
// constants/brand.js.
const AffiliateTerms = lazy(() => import("pages/AffiliateProgram/AffiliateTerms"));
const BlogListing = lazy(() => import("pages/Blog"));
const BlogPost = lazy(() => import("pages/Blog/BlogPost"));
// Public per-tenant business pages (root-level slug). The CloudFront function
// serves the SPA shell for unknown single-segment paths; the component
// resolves the slug via the public API and renders NotFound (noindex) when
// the slug doesn't exist.
const BusinessPage = lazy(() => import("pages/BusinessPage"));

// Route definitions (privacy, terms, affiliates)
import routes from "routes";

// ---------------------------------------------------------------------------
// Brand dispatch — resolved at build time from REACT_APP_BRAND.
// Throws at module load on unknown/missing values. No runtime hostname inference.
// ---------------------------------------------------------------------------
const BRAND = process.env.REACT_APP_BRAND;

let RootPage;
let API_BASE_URL;
switch (BRAND) {
  case "ringfoods":
    RootPage = RingFoodsHomepage;
    API_BASE_URL = "https://us-west.ringfoods.com";
    break;
  case "ringoperator":
    RootPage = RingOperatorHomepage;
    // Shared backend — tracking uses the same ringfoods API host.
    API_BASE_URL = "https://us-west.ringfoods.com";
    break;
  default:
    throw new Error(
      `Unknown or missing REACT_APP_BRAND: '${BRAND}'. Must be 'ringfoods' or 'ringoperator'.`
    );
}

// Strip trailing slashes to prevent duplicate-URL indexing.
// Returns a <Navigate> for paths like "/blog/foo/" → "/blog/foo".
function TrailingSlashRedirect() {
  const { pathname, search, hash } = useLocation();
  if (pathname !== "/" && pathname.endsWith("/")) {
    return <Navigate to={pathname.slice(0, -1) + search + hash} replace />;
  }
  return null;
}

export default function App() {
  const { pathname, search } = useLocation();

  // Reveal page once React has hydrated (removes the prerender-hide style)
  useEffect(() => {
    const hideStyle = document.getElementById("prerender-hide");
    if (hideStyle) hideStyle.remove();
  }, []);

  // Setting page scroll to 0 when changing the route (unless there's a hash anchor)
  useEffect(() => {
    if (!window.location.hash) {
      document.documentElement.scrollTop = 0;
      document.scrollingElement.scrollTop = 0;
    } else {
      const id = window.location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [pathname]);

  // Track page visit (once per day per browser, fire-and-forget).
  // Dedup key is brand-scoped so both sites track independently in the same browser.
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const dedupKey = `${BRAND}_visit_homepage`;
    if (localStorage.getItem(dedupKey) !== today) {
      localStorage.setItem(dedupKey, today);
      fetch(`${API_BASE_URL}/api/v1/track-visit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ page: "homepage", brand: BRAND }),
      }).catch(() => {});
    }
  }, []);

  // Track referral link click (fire-and-forget, sessionStorage dedup)
  // Ref code is NOT persisted — it flows through URL params only
  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const refCode = searchParams.get("ref");
    if (refCode) {
      const dedupKey = `${BRAND}_click_tracked_${refCode}`;
      if (!sessionStorage.getItem(dedupKey)) {
        sessionStorage.setItem(dedupKey, "1");
        fetch(`${API_BASE_URL}/api/v1/affiliates/track-click`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ affiliate_code: refCode, brand: BRAND }),
        }).catch(() => {});
      }
    }
  }, [search]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <TrailingSlashRedirect />
      <Suspense fallback={null}>
        <Routes>
          {getRoutes(routes)}
          <Route path="/" element={<RootPage />} />
          {/* /login has no SPA route — the CloudFront viewer-request function
              301s it to app.<brand>.com/dashboard/login before it reaches React. */}
          <Route path="/affiliates/terms" element={<AffiliateTerms />} />
          <Route path="/blog" element={<BlogListing />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          {/* Root-level business slug — RRv6 route ranking keeps every static
              route above winning; only unmatched single segments land here. */}
          <Route path="/:business_slug" element={<BusinessPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </ThemeProvider>
  );
}
